import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Grid } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { renderField, renderListView, renderSelectedDataElement } from './fieldRenderers';
import { renderConditionsTable, renderLayoutTable, renderExternalFilters, renderSplitPane, renderBlocksTable } from './tableRenderers';
import { DataElementChips } from './components/DataElementChips';
import { SummaryItemsRenderer } from './components/SummaryItemsRenderer';
import { SelectedInnerDataElementRenderer } from './components/SelectedInnerDataElementRenderer';
import { FieldRouter } from './components/FieldRouter';

// Render selectedTopLevelDataElement as chips
export const renderSelectedTopLevelDataElement = (dataElementArray, classes) => {
	if (!Array.isArray(dataElementArray) || dataElementArray.length === 0) {
		return <Typography color="textSecondary">(Empty)</Typography>;
	}

	const values = dataElementArray
		.map((item) => {
			if (typeof item === 'object' && item !== null) {
				return item.value || item.label || item.name || JSON.stringify(item);
			}
			return String(item);
		})
		.filter((val) => val !== undefined && val !== null);

	if (values.length === 0) {
		return <Typography color="textSecondary">(Empty)</Typography>;
	}

	return (
		<Box display="flex" flexWrap="wrap" mt={0.5}>
			{values.map((val, index) => (
				<Chip
					key={index}
					label={val}
					className={classes?.chip}
					size="small"
					style={{ marginRight: 8, marginBottom: 8 }}
				/>
			))}
		</Box>
	);
};

// Render lineItems or summaryItems as a table
export const renderItemsObject = (itemsObj, classes) => {
	if (!itemsObj || typeof itemsObj !== 'object' || Array.isArray(itemsObj)) {
		return <Typography color="textSecondary">(No data)</Typography>;
	}

	const entries = Object.entries(itemsObj);
	if (entries.length === 0) {
		return <Typography color="textSecondary">(Empty)</Typography>;
	}

	return (
		<TableContainer component={Paper} style={{ marginTop: 8 }}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>Property</TableCell>
						<TableCell>Value</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{entries.map(([propKey, propValue], index) => {
						// Special handling for selectedInnerDataElement - display as chips
						const isSelectedInnerDataElement = propKey === 'selectedInnerDataElement' && Array.isArray(propValue);

						return (
							<TableRow key={index}>
								<TableCell>{propKey}</TableCell>
								<TableCell>
									{isSelectedInnerDataElement ? (
										// Render as chips
										<SelectedInnerDataElementRenderer
											selectedInnerDataElement={propValue}
											classes={classes}
											asChips={true}
											showLabel={false}
										/>
									) : typeof propValue === 'object' && propValue !== null && !Array.isArray(propValue)
										? JSON.stringify(propValue, null, 2)
										: Array.isArray(propValue)
											? propValue.length === 0
												? '(Empty array)'
												: propValue.map((item, idx) => {
													if (typeof item === 'object' && item !== null && item.value) {
														return item.value;
													}
													return String(item);
												}).filter((v) => v !== undefined && v !== null).join(', ')
											: String(propValue !== undefined && propValue !== null ? propValue : '-')}
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

// Steps component with tabs (non-accordion)
const StepsTabs = ({ stepsArray, classes }) => {
	const [activeTab, setActiveTab] = useState(0);

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	return (
		<Box>
			<Tabs
				value={activeTab}
				onChange={handleTabChange}
				variant="scrollable"
				scrollButtons="auto"
				style={{ marginBottom: 16, borderBottom: '1px solid #e0e0e0' }}
			>
				{stepsArray.map((step, index) => (
					<Tab
						key={index}
						label={`Step ${index + 1}${step && typeof step === 'object' && step.name ? `: ${step.name}` : ''}`}
					/>
				))}
			</Tabs>

			{stepsArray.map((step, index) => (
				<Box key={index} role="tabpanel" hidden={activeTab !== index}>
					{activeTab === index && (
						<Box>
							{typeof step === 'object' && step !== null ? (
								(() => {
									// Define the fields we want to show in the table (in order)
									const tableFields = ['enable', 'type', 'name', 'label', 'title'];
									const tableRows = [];

									// Create rows for tableFields
									tableFields.forEach((field) => {
										if (step[field] !== undefined && step[field] !== null) {
											let displayValue;
											if (field === 'enable' && typeof step[field] === 'boolean') {
												displayValue = step[field] ? (
													<Box display="flex" alignItems="center">
														<CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} fontSize="small" />
														<Typography component="span">true</Typography>
													</Box>
												) : (
													<Box display="flex" alignItems="center">
														<CancelIcon style={{ color: '#f44336', marginRight: 8 }} fontSize="small" />
														<Typography component="span">false</Typography>
													</Box>
												);
											} else {
												displayValue = <FieldRouter fieldKey={field} value={step[field]} classes={classes} showLabel={false} />;
											}
											tableRows.push({
												property: field,
												value: displayValue || String(step[field] !== undefined && step[field] !== null ? step[field] : '-')
											});
										}
									});

									// Get remaining fields that aren't in tableFields
									const remainingFields = Object.entries(step).filter(([key]) => !tableFields.includes(key));

									// Separate selectedDataElement, summaryItems, and lineItems
									const selectedDataElement = step.selectedDataElement;
									const summaryItems = step.summaryItems;
									const lineItems = step.lineItems;
									const otherFields = remainingFields.filter(([key]) =>
										key !== 'selectedDataElement' && key !== 'summaryItems' && key !== 'lineItems'
									);

									return (
										<Box>
											{tableRows.length > 0 && (
												<TableContainer component={Paper} style={{ marginTop: 8, marginBottom: 16 }}>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell style={{ fontWeight: 600, width: '25%' }}>Property</TableCell>
																<TableCell style={{ fontWeight: 600, width: '25%' }}>Value</TableCell>
																<TableCell style={{ fontWeight: 600, width: '25%' }}></TableCell>
																<TableCell style={{ fontWeight: 600, width: '25%' }}></TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{tableRows.map((row, idx) => (
																<TableRow key={idx}>
																	<TableCell>{row.property}:</TableCell>
																	<TableCell colSpan={3}>{row.value}</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</TableContainer>
											)}

											{/* selectedDataElement right after top table */}
											{selectedDataElement && Array.isArray(selectedDataElement) && (
												<Box mt={2} mb={2}>
													<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
														selectedDataElement:
													</Typography>
													<TableContainer component={Paper} style={{ marginTop: 8 }}>
														<Table size="small">
															<TableHead>
																<TableRow>
																	<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
																	<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
																</TableRow>
															</TableHead>
															<TableBody>
																<TableRow>
																	<TableCell>selectedDataElement:</TableCell>
																	<TableCell>
																		<Box display="flex" flexWrap="wrap">
																			{selectedDataElement.map((item, index) => {
																				const value = typeof item === 'object' && item !== null ? (item.value || item.label || item.name) : String(item);
																				if (!value) return null;
																				return (
																					<Chip
																						key={index}
																						label={value}
																						size="small"
																						style={{ marginRight: 8, marginBottom: 8 }}
																					/>
																				);
																			})}
																		</Box>
																	</TableCell>
																</TableRow>
															</TableBody>
														</Table>
													</TableContainer>
												</Box>
											)}

											{/* summaryItems (labeled as "summary fields") */}
											{summaryItems && typeof summaryItems === 'object' && (
												<Box mt={3} mb={2}>
													<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
														summary fields:
													</Typography>
													<SummaryItemsRenderer
														summaryItems={summaryItems}
														classes={classes}
														showLabel={false}
													/>
												</Box>
											)}

											{/* lineItems */}
											{lineItems && typeof lineItems === 'object' && (
												<Box mt={3} mb={2}>
													<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
														lineItems:
													</Typography>
													{(lineItems.title || (lineItems.selectedDataElement && Array.isArray(lineItems.selectedDataElement)) || (lineItems.selectedInnerDataElement && Array.isArray(lineItems.selectedInnerDataElement))) && (
														<Box mb={2}>
															<TableContainer component={Paper} style={{ marginTop: 8 }}>
																<Table size="small">
																	<TableHead>
																		<TableRow>
																			<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
																			<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
																		</TableRow>
																	</TableHead>
																	<TableBody>
																		{lineItems.title && (
																			<TableRow>
																				<TableCell>title:</TableCell>
																				<TableCell>{lineItems.title}</TableCell>
																			</TableRow>
																		)}
																		{lineItems.selectedDataElement && Array.isArray(lineItems.selectedDataElement) && (
																			<TableRow>
																				<TableCell>selectedDataElement:</TableCell>
																				<TableCell>
																					<Box display="flex" flexWrap="wrap">
																						{lineItems.selectedDataElement.map((item, index) => {
																							const value = typeof item === 'object' && item !== null ? (item.value || item.label || item.name) : String(item);
																							if (!value) return null;
																							return (
																								<Chip
																									key={index}
																									label={value}
																									size="small"
																									style={{ marginRight: 8, marginBottom: 8 }}
																								/>
																							);
																						})}
																					</Box>
																				</TableCell>
																			</TableRow>
																		)}
																		{lineItems.selectedInnerDataElement && Array.isArray(lineItems.selectedInnerDataElement) && (
																			<TableRow>
																				<TableCell>selectedInnerDataElement:</TableCell>
																				<TableCell>
																					<SelectedInnerDataElementRenderer
																						selectedInnerDataElement={lineItems.selectedInnerDataElement}
																						classes={classes}
																						asChips={true}
																						showLabel={false}
																					/>
																				</TableCell>
																			</TableRow>
																		)}
																	</TableBody>
																</Table>
															</TableContainer>
														</Box>
													)}
													{/* Display other keys in lineItems as table */}
													{(() => {
														const otherKeys = Object.entries(lineItems).filter(
															([key]) => key !== 'title' && key !== 'selectedDataElement' && key !== 'selectedInnerDataElement'
														);
														if (otherKeys.length === 0) return null;
														return (
															<Box mt={2}>
																<TableContainer component={Paper} style={{ marginTop: 8 }}>
																	<Table size="small">
																		<TableHead>
																			<TableRow>
																				<TableCell style={{ fontWeight: 600 }}>Property</TableCell>
																				<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
																			</TableRow>
																		</TableHead>
																		<TableBody>
																			{otherKeys.map(([key, value], idx) => (
																				<TableRow key={idx}>
																					<TableCell>{key}:</TableCell>
																					<TableCell>
																						{typeof value === 'object' && value !== null ? (
																							JSON.stringify(value, null, 2)
																						) : (
																							String(value !== undefined && value !== null ? value : '-')
																						)}
																					</TableCell>
																				</TableRow>
																			))}
																		</TableBody>
																	</Table>
																</TableContainer>
															</Box>
														);
													})()}
												</Box>
											)}

											{/* Other remaining fields */}
											{otherFields.length > 0 && (
												<Box mt={2}>
													{otherFields.map(([key, value]) => (
														<Box key={key} mb={1}>
															<Typography variant="subtitle2" className={classes.fieldLabel} style={{ minWidth: 180 }}>
																{key}:
															</Typography>
															<Box mt={0.5}>
																<FieldRouter fieldKey={key} value={value} classes={classes} showLabel={false} />
															</Box>
														</Box>
													))}
												</Box>
											)}
										</Box>
									);
								})()
							) : (
								<Typography>{String(step)}</Typography>
							)}
						</Box>
					)}
				</Box>
			))}
		</Box>
	);
};

// Render steps with tabs (non-accordion)
export const renderSteps = (stepsArray, classes) => {
	if (!Array.isArray(stepsArray) || stepsArray.length === 0) {
		return <Typography color="textSecondary">(No steps)</Typography>;
	}

	return <StepsTabs stepsArray={stepsArray} classes={classes} />;
};

// Render tabsConfig without accordion (direct display)
export const renderTabsConfig = (tabsConfig, classes) => {
	if (!tabsConfig || typeof tabsConfig !== 'object') {
		return <Typography color="textSecondary">(No tabsConfig data)</Typography>;
	}

	return (
		<Box>
			{Object.entries(tabsConfig).map(([key, value]) => {
				return (
					<Box key={key} mb={2}>
						{renderField(`tabsConfig.${key}`, value, classes)}
					</Box>
				);
			})}
		</Box>
	);
};

// Render compareLineItemsAndPOInfo with custom structure
export const renderCompareLineItemsAndPOInfo = (compareData, classes) => {
	if (!compareData || typeof compareData !== 'object') {
		return <Typography color="textSecondary">(No compareLineItemsAndPOInfo data)</Typography>;
	}

	return (
		<Box>
			{/* Enable and Title outside table */}
			{compareData.enable !== undefined && compareData.enable !== null && (
				<Box mb={2} display="flex" alignItems="center">
					<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
						enable:
					</Typography>
					<Typography component="span">{String(compareData.enable)}</Typography>
				</Box>
			)}
			{compareData.title && (
				<Box mb={2} display="flex" alignItems="center">
					<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
						title:
					</Typography>
					<Typography component="span">{compareData.title}</Typography>
				</Box>
			)}

			{/* selectedDataElement at top level */}
			{compareData.selectedDataElement && Array.isArray(compareData.selectedDataElement) && (
				<Box mt={3} mb={2}>
					<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
						selectedDataElement:
					</Typography>
					<Box mb={2}>
						<TableContainer component={Paper} style={{ marginTop: 8 }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
										<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow>
										<TableCell>selectedDataElement:</TableCell>
										<TableCell>
											<Box display="flex" flexWrap="wrap">
												{compareData.selectedDataElement.map((item, index) => {
													const value = typeof item === 'object' && item !== null ? (item.value || item.label || item.name) : String(item);
													if (!value) return null;
													return (
														<Chip
															key={index}
															label={value}
															size="small"
															style={{ marginRight: 8, marginBottom: 8 }}
														/>
													);
												})}
											</Box>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				</Box>
			)}

			{/* summaryItems as subheading */}
			{compareData.summaryItems && typeof compareData.summaryItems === 'object' && (
				<Box mt={3} mb={2}>
					<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
						summaryItems:
					</Typography>
					{compareData.summaryItems.selectedDataElement && Array.isArray(compareData.summaryItems.selectedDataElement) && (
						<Box mb={2}>
							<TableContainer component={Paper} style={{ marginTop: 8 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
											<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										<TableRow>
											<TableCell>selectedDataElement:</TableCell>
											<TableCell>
												<Box display="flex" flexWrap="wrap">
													{compareData.summaryItems.selectedDataElement.map((item, index) => {
														const value = typeof item === 'object' && item !== null ? (item.value || item.label || item.name) : String(item);
														if (!value) return null;
														return (
															<Chip
																key={index}
																label={value}
																size="small"
																style={{ marginRight: 8, marginBottom: 8 }}
															/>
														);
													})}
												</Box>
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
					{/* Display other keys in summaryItems as table */}
					{(() => {
						const otherKeys = Object.entries(compareData.summaryItems).filter(
							([key]) => key !== 'selectedDataElement'
						);
						if (otherKeys.length === 0) return null;
						return (
							<Box mt={2}>
								<TableContainer component={Paper} style={{ marginTop: 8 }}>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell style={{ fontWeight: 600 }}>Property</TableCell>
												<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{otherKeys.map(([key, value], idx) => (
												<TableRow key={idx}>
													<TableCell>{key}:</TableCell>
													<TableCell>
														{typeof value === 'object' && value !== null ? (
															JSON.stringify(value, null, 2)
														) : (
															String(value !== undefined && value !== null ? value : '-')
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						);
					})()}
				</Box>
			)}

			{/* lineItems as subheading */}
			{compareData.lineItems && typeof compareData.lineItems === 'object' && (
				<Box mt={3} mb={2}>
					<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
						lineItems:
					</Typography>
					{(compareData.lineItems.title || (compareData.lineItems.selectedDataElement && Array.isArray(compareData.lineItems.selectedDataElement)) || (compareData.lineItems.selectedInnerDataElement && Array.isArray(compareData.lineItems.selectedInnerDataElement))) && (
						<Box mb={2}>
							<TableContainer component={Paper} style={{ marginTop: 8 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
											<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{compareData.lineItems.title && (
											<TableRow>
												<TableCell>title:</TableCell>
												<TableCell>{compareData.lineItems.title}</TableCell>
											</TableRow>
										)}
										{compareData.lineItems.selectedDataElement && Array.isArray(compareData.lineItems.selectedDataElement) && (
											<TableRow>
												<TableCell>selectedDataElement:</TableCell>
												<TableCell>
													<Box display="flex" flexWrap="wrap">
														{compareData.lineItems.selectedDataElement.map((item, index) => {
															const value = typeof item === 'object' && item !== null ? (item.value || item.label || item.name) : String(item);
															if (!value) return null;
															return (
																<Chip
																	key={index}
																	label={value}
																	size="small"
																	style={{ marginRight: 8, marginBottom: 8 }}
																/>
															);
														})}
													</Box>
												</TableCell>
											</TableRow>
										)}
										{compareData.lineItems.selectedInnerDataElement && Array.isArray(compareData.lineItems.selectedInnerDataElement) && (
											<TableRow>
												<TableCell>selectedInnerDataElement:</TableCell>
												<TableCell>
													<SelectedInnerDataElementRenderer
														selectedInnerDataElement={compareData.lineItems.selectedInnerDataElement}
														classes={classes}
														asChips={true}
														showLabel={false}
													/>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
					{/* Display other keys in lineItems as table (excluding selectedInnerDataElement) */}
					{(() => {
						const otherKeys = Object.entries(compareData.lineItems).filter(
							([key]) => key !== 'title' && key !== 'selectedDataElement' && key !== 'selectedInnerDataElement'
						);
						if (otherKeys.length === 0) return null;
						return (
							<Box mt={2}>
								<TableContainer component={Paper} style={{ marginTop: 8 }}>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell style={{ fontWeight: 600 }}>Property</TableCell>
												<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{otherKeys.map(([key, value], idx) => (
												<TableRow key={idx}>
													<TableCell>{key}:</TableCell>
													<TableCell>
														{typeof value === 'object' && value !== null ? (
															JSON.stringify(value, null, 2)
														) : (
															String(value !== undefined && value !== null ? value : '-')
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						);
					})()}
				</Box>
			)}

			{/* poLineItems as subheading */}
			{compareData.poLineItems && typeof compareData.poLineItems === 'object' && (
				<Box mt={3} mb={2}>
					<Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginBottom: 12, fontSize: '1rem' }}>
						poLineItems:
					</Typography>
					{(compareData.poLineItems.title || (compareData.poLineItems.selectedDataElement && Array.isArray(compareData.poLineItems.selectedDataElement)) || (compareData.poLineItems.selectedInnerDataElement && Array.isArray(compareData.poLineItems.selectedInnerDataElement))) && (
						<Box mb={2}>
							<TableContainer component={Paper} style={{ marginTop: 8 }}>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell style={{ fontWeight: 600, width: '30%' }}>Property</TableCell>
											<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{compareData.poLineItems.title && (
											<TableRow>
												<TableCell>title:</TableCell>
												<TableCell>{compareData.poLineItems.title}</TableCell>
											</TableRow>
										)}
										{compareData.poLineItems.selectedDataElement && Array.isArray(compareData.poLineItems.selectedDataElement) && (
											<TableRow>
												<TableCell>selectedDataElement:</TableCell>
												<TableCell>
													<Box display="flex" flexWrap="wrap">
														{compareData.poLineItems.selectedDataElement.map((item, index) => {
															const value = typeof item === 'object' && item !== null ? (item.value || item.label || item.name) : String(item);
															if (!value) return null;
															return (
																<Chip
																	key={index}
																	label={value}
																	size="small"
																	style={{ marginRight: 8, marginBottom: 8 }}
																/>
															);
														})}
													</Box>
												</TableCell>
											</TableRow>
										)}
										{compareData.poLineItems.selectedInnerDataElement && Array.isArray(compareData.poLineItems.selectedInnerDataElement) && (
											<TableRow>
												<TableCell>selectedInnerDataElement:</TableCell>
												<TableCell>
													<SelectedInnerDataElementRenderer
														selectedInnerDataElement={compareData.poLineItems.selectedInnerDataElement}
														classes={classes}
														asChips={true}
														showLabel={false}
													/>
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
					{/* Display other keys in poLineItems as table (excluding selectedInnerDataElement) */}
					{(() => {
						const otherKeys = Object.entries(compareData.poLineItems).filter(
							([key]) => key !== 'title' && key !== 'selectedDataElement' && key !== 'selectedInnerDataElement'
						);
						if (otherKeys.length === 0) return null;
						return (
							<Box mt={2}>
								<TableContainer component={Paper} style={{ marginTop: 8 }}>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell style={{ fontWeight: 600 }}>Property</TableCell>
												<TableCell style={{ fontWeight: 600 }}>Value</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{otherKeys.map(([key, value], idx) => (
												<TableRow key={idx}>
													<TableCell>{key}:</TableCell>
													<TableCell>
														{typeof value === 'object' && value !== null ? (
															JSON.stringify(value, null, 2)
														) : (
															String(value !== undefined && value !== null ? value : '-')
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						);
					})()}
				</Box>
			)}
		</Box>
	);
};

// Render headerTableConfig without accordion (direct display)
export const renderHeaderTableConfig = (headerTableConfig, classes) => {
	if (!headerTableConfig || typeof headerTableConfig !== 'object') {
		return <Typography color="textSecondary">(No headerTableConfig data)</Typography>;
	}

	return (
		<Box>
			{Object.entries(headerTableConfig).map(([key, value]) => {
				// Special handling for selectedDataElement array - display as chips
				if (key === 'selectedDataElement' && Array.isArray(value)) {
					const values = value
						.map((item) => {
							if (typeof item === 'object' && item !== null && item.value) {
								return item.value;
							}
							return String(item);
						})
						.filter((val) => val !== undefined && val !== null);

					return (
						<Box key={key} mb={2} display="flex" alignItems="center" flexWrap="wrap">
							<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
								{key}:
							</Typography>
							{values.length === 0 ? (
								<Typography color="textSecondary">(Empty)</Typography>
							) : (
								<Box display="flex" flexWrap="wrap">
									{values.map((val, index) => (
										<Chip
											key={index}
											label={val}
											className={classes.chip}
											size="small"
											style={{ marginRight: 8, marginBottom: 8 }}
										/>
									))}
								</Box>
							)}
						</Box>
					);
				}
				// Special handling for headerTableConditionConfig - render as table without accordion
				if (key === 'headerTableConditionConfig' && typeof value === 'object' && value !== null) {
					return (
						<Box key={key} mb={2}>
							<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180, marginBottom: 8 }}>
								{key}:
							</Typography>
							{renderItemsObject(value, classes)}
						</Box>
					);
				}
				// Simple inline format - no duplicate labels
				return (
					<Box key={key} mb={1} display="flex" alignItems="center">
						<Typography component="span" className={classes.fieldLabel} style={{ minWidth: 180 }}>
							{key}:
						</Typography>
						<FieldRouter fieldKey={key} value={value} classes={classes} showLabel={false} />
					</Box>
				);
			})}
		</Box>
	);
};
